import { handleShowToastError } from '@utils/helpers';
import _ from 'lodash';
import { FormInstance } from 'antd/lib';
import {
  COMMON_MESSAGE_ERROR,
  COMPANY_DEFAULT_OPTION,
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
} from '@constants/common';
import { EModalType } from 'types/others';
import { EHttpStatusCode } from 'types/service';
import { TAxiosResponse } from 'types';
import { appToast } from '@utils/appToast';
import {
  TCheckCompanyExistReq,
  TFormCompanyReq,
  TFormModalRegisterReq,
  TFormModalRes,
  TFormModalUpdateReq,
  TListCompanyRes,
  TListCompanyTable,
  TSelectValue,
} from '@models/company';
import { companyApi } from '@services/companyApi';
import { COMPANY_CONSTANT } from '../constants';
import { TSearchCompanyParam } from 'types/types';
import { COMMON_CONSTANT, USER_ROLE } from '@utils/constants';
import { useAppSelector } from '@config/hooks';
import { useCurrentUserRole } from '@hooks';

const { NAME, MESSAGE, TITLE, FORM_NAME } = COMPANY_CONSTANT;

function useCompanyApi() {
  const currentUserRole = useCurrentUserRole();
  // tạm dùng storeCode vì modal đang không có id
  const storeId = useAppSelector((state) =>
    state.auth.currentUser != null ? state.auth.currentUser?.storeCode : ''
  ) as any;
  // API
  const registrationCompany = async (
    params: TFormModalRegisterReq,
    handleOkModalSuccess: () => void,
    setLoadingPage: (val: boolean) => void,
    handleModalFail: (description?: string) => void,
    handleCommonError: () => void
  ) => {
    setLoadingPage(true);

    try {
      const response = await companyApi.registrationCompany(params);
      if (response.ok) {
        handleOkModalSuccess();
      }
    } catch (error: any) {
      handleCommonError();
      if (
        error?.status === EHttpStatusCode.BAD_REQUEST &&
        error?.data?.errors?.[0]?.description
      ) {
        handleModalFail(error?.data?.errors?.[0]?.description);
        return;
      }
      const err = error as TAxiosResponse;
      if (err.status === EHttpStatusCode.FORBIDDEN) {
        appToast.error({ message: COMMON_MESSAGE_ERROR });
        return;
      }
      handleShowToastError(error, '');
    } finally {
      setLoadingPage(false);
    }
  };

  const updateCompany = async (
    params: TFormModalUpdateReq,
    handleOkModalSuccess: () => void,
    setLoadingPage: (val: boolean) => void,
    handleModalFail: (description?: string) => void,
    handleCommonError: () => void
  ) => {
    setLoadingPage(true);

    try {
      const response = await companyApi.updateCompany(params);
      if (response.ok) {
        handleOkModalSuccess();
      }
    } catch (error: any) {
      handleCommonError();
      if (
        error?.status === EHttpStatusCode.BAD_REQUEST &&
        error?.data?.errors?.[0]?.description
      ) {
        handleModalFail(error?.data?.errors?.[0]?.description);
        return;
      }
      const err = error as TAxiosResponse;
      if (err.status === EHttpStatusCode.FORBIDDEN) {
        appToast.error({ message: COMMON_MESSAGE_ERROR });
        return;
      }
      handleShowToastError(error, '');
    } finally {
      setLoadingPage(false);
    }
  };

  const detailCompany = async (
    payload: string,
    handleSetFormValues: (val: TFormModalRes) => void,
    setLoadingModal: (val: boolean) => void
  ) => {
    setLoadingModal(true);
    try {
      const response = await companyApi.getDetailCompany(payload);

      if (response.ok) {
        const { data } = response;
        handleSetFormValues({ ...data, id: payload });
      }
    } catch (error) {
      handleShowToastError(error, '');
      handleSetFormValues({
        id: '',
        companyCode: '',
        companyName: '',
      });
    } finally {
      setLoadingModal(false);
    }
  };

  const deleteCompany = async (
    payload: string,
    listCompany: TListCompanyTable[],
    setListCompany: (val: TListCompanyTable[]) => void,
    setLoadingPage: (val: boolean) => void,
    setModalSuccess: (val: boolean) => void,
    setModalType: (val: EModalType | null | string) => void,
    handleCloseModalDelete: () => void,
    handleDeleteFail: () => void
  ) => {
    setLoadingPage(true);

    try {
      const response = await companyApi.deleteCompany(payload);
      if (response.ok) {
        setListCompany(
          [
            ..._.filter(listCompany, function (item) {
              return item.id !== payload;
            }),
          ].map((item, index) => ({ ...item, key: index + 1 }))
        );

        setModalSuccess(true);
        setModalType(EModalType.SUCCESS_DELETE);
        handleCloseModalDelete();
      }
    } catch (error: any) {
      if (
        error?.status === EHttpStatusCode.BAD_REQUEST &&
        error?.data?.errors?.[0]?.description === 'CompanyNotExist'
      ) {
        handleDeleteFail();
        setModalType(TITLE.NOT_FOUND_COMPANY);
        return;
      }
      handleShowToastError(error, '');
    } finally {
      setLoadingPage(false);
    }
  };

  const checkExistCompany = async (
    payload: TCheckCompanyExistReq,
    formModal: FormInstance,
    modalType: EModalType,
    setModalType: (val: EModalType | null | string) => void,
    setLoadingPage: (val: boolean) => void,
    handleCheckFoundBeforeUpdate: () => void
  ) => {
    setLoadingPage(true);
    try {
      const response = await companyApi.checkCompanyExist(payload);
      if (response.ok) {
        setModalType(modalType);
      }
    } catch (error: any) {
      if (
        error.status === EHttpStatusCode.BAD_REQUEST &&
        error?.data?.errors.length
      ) {
        switch (error?.data?.errors[0].description) {
          case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
            .COMPANY_ALREADY_EXISTED:
            formModal.setFields([
              {
                name: [NAME.COMPANY_CODE],
                errors: [MESSAGE.DATA_EXIST],
              },
            ]);
            return;
          case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.COMPANY_NOT_EXIST:
            handleCheckFoundBeforeUpdate();
            return;
          default:
            return;
        }
      }

      handleShowToastError(error, '');
    } finally {
      setLoadingPage(false);
    }
  };

  const getListCompanySearch = async (
    payload: TSearchCompanyParam,
    setLoadingPage: (val: boolean) => void,
    setListCompany: (val: TListCompanyTable[]) => void,
    handleSetSearchParams: (val: TSearchCompanyParam) => void,
    setPageIndex: (val: number) => void,
    setTotalRecords: (val: number) => void
  ) => {
    setLoadingPage(true);

    try {
      const response = await companyApi.searchCompany(payload);
      if (response.ok) {
        setListCompany(
          response.data.companies.map((item, index) => ({
            ...item,
            key: index + 1,
          }))
        );
        if (payload.companyId) {
          handleSetSearchParams(payload);
        } else {
          handleSetSearchParams({ ...payload });
        }
        setPageIndex(1);
        setTotalRecords(Number(response.pagination?.totalCount));
      }
    } catch (error) {
      setPageIndex(0);
      setTotalRecords(0);
      handleShowToastError(error, '');
    } finally {
      setLoadingPage(false);
    }
  };

  const getListCompanyScroll = async (
    payload: TSearchCompanyParam,
    listCompany: TListCompanyTable[],
    pageIndex: number,
    setLoadingPage: (val: boolean) => void,
    setListCompany: (val: any) => void,
    setPageIndex: (val: number) => void,
    setTotalRecords: (val: number) => void
  ) => {
    setLoadingPage(true);
    try {
      const response = await companyApi.searchCompany(payload);

      if (response.ok) {
        setTotalRecords(Number(response.pagination?.totalCount));

        if (listCompany.length !== response.pagination?.totalCount) {
          const startIndex = listCompany.length;
          setListCompany((prevData: TListCompanyRes[]) => [
            ...prevData,
            ...response.data.companies.map((item, index) => ({
              ...item,
              key: startIndex + index + 1,
            })),
          ]);
          setPageIndex(pageIndex + 1);
        }
      }
    } catch (error) {
      handleShowToastError(error, '');
    } finally {
      setLoadingPage(false);
    }
  };

  const fetchInitialApis = async (
    _pageIndex: number,
    setLoadingPage: (val: boolean) => void,
    setLoadingCompany: (val: boolean) => void,
    _handleSetSearchParams: (val: TFormCompanyReq) => void,
    setCompanies: (val: TSelectValue[]) => void,
    setListCompany: (val: TListCompanyTable[]) => void,
    setCompaniesModal: (val: TSelectValue[]) => void,
    setTotalRecords: (val: number) => void,
    setPageIndex: (val: number) => void
  ) => {
    setLoadingPage(true);

    try {
      const resCompany = await companyApi.getCompanies({
        storeId: currentUserRole === USER_ROLE.STORE_OWNER ? storeId : '',
      });
      if (resCompany.ok) {
        const convertCompanies = resCompany.data.map((val) => ({
          value: val.id,
          label: val.companyCode + '_' + val.companyName,
        }));
        setCompanies([COMPANY_DEFAULT_OPTION, ...convertCompanies]);
        setCompaniesModal(
          convertCompanies.map((item) => ({
            ...item,
            value: item.value,
            children: item.value,
          }))
        );

        const response = await companyApi.searchCompany({
          companyId: COMPANY_DEFAULT_OPTION.value,
          page: DEFAULT_PAGE_NUMBER,
          pageSize: DEFAULT_PAGE_SIZE,
        });
        if (response.ok) {
          const convertListCompany: TListCompanyTable[] =
            response.data.companies.map((item, index) => ({
              ...item,
              key: index + 1,
            }));
          setListCompany(convertListCompany);
          setPageIndex(1);
          setTotalRecords(Number(response.pagination?.totalCount));
        }
      }
    } catch (error) {
      setListCompany([]);
      setPageIndex(0);
      setTotalRecords(0);

      handleShowToastError(error, '');
    } finally {
      setLoadingPage(false);
      setLoadingCompany(false);
    }
  };

  const fetchApiRedirect = async (
    payload: TFormCompanyReq,
    page: number,
    setLoadingPage: (val: boolean) => void,
    setLoadingCompany: (val: boolean) => void,
    _handleSetSearchParams: (val: TFormCompanyReq) => void,
    setCompanies: (val: TSelectValue[]) => void,
    setListCompany: (val: TListCompanyTable[]) => void,
    setCompaniesModal: (val: TSelectValue[]) => void,
    setTotalRecords: (val: number) => void,
    setPageIndex: (val: number) => void
  ) => {
    setLoadingPage(true);

    try {
      const resCompany = await companyApi.getCompanies({
        storeId: currentUserRole === USER_ROLE.STORE_OWNER ? storeId : '',
      });
      if (resCompany.ok) {
        const convertCompanies = resCompany.data.map((val) => ({
          value: val.id,
          label: val.companyName,
        }));
        setCompanies([COMPANY_DEFAULT_OPTION, ...convertCompanies]);
        setCompaniesModal(
          convertCompanies.map((item) => ({
            ...item,
            value: item.value,
            children: item.value,
          }))
        );
      }

      const response = await companyApi.searchCompany({
        ...payload,
        page,
      });
      if (response.ok) {
        const convertListCompany: TListCompanyTable[] =
          response.data.companies.map((item, index) => ({
            ...item,
            key: index + 1,
          }));
        setListCompany(convertListCompany);
        setPageIndex(1);
        setTotalRecords(Number(response.pagination?.totalCount));
      }
    } catch (error) {
      setListCompany([]);
      setPageIndex(0);
      setTotalRecords(0);
      handleShowToastError(error, '');
    } finally {
      setLoadingPage(false);
      setLoadingCompany(false);
    }
  };

  // EFFECT
  const effectCompany = (companyParam: string, form: FormInstance) => {
    form.setFieldValue(
      FORM_NAME.COMPANY,
      companyParam || COMPANY_DEFAULT_OPTION.value
    );
  };

  return {
    handler: {
      fetchInitialApis,
      fetchApiRedirect,
      getListCompanyScroll,
      getListCompanySearch,
      registrationCompany,
      detailCompany,
      checkExistCompany,
      deleteCompany,
      updateCompany,
    },
    effect: {
      effectCompany,
    },
  };
}

export default useCompanyApi;
